import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { Video, VIDEO_TYPES } from 'components/atoms';
import { VideoPlayer } from '@spotify-internal/encore-web/ads-business/components/VideoPlayer/V2';
import { ASPECT_RATIOS } from '@spotify-internal/encore-web/ads-business/utils/config';
import * as Styled from './VideoSection.styled';

const VideoSection = ({
  type,
  videoId,
  videoName,
  caption,
  video,
  orientation,
  pageContext = '',
  subtitlesFile,
}) => {
  const videoContainerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Check if fullscreen API is supported
  const isFullscreenSupported = () => {
    return !!(
      document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.mozFullScreenEnabled ||
      document.msFullscreenEnabled
    );
  };

  // Enter fullscreen mode
  const enterFullscreen = async () => {
    if (!videoContainerRef.current || !isFullscreenSupported()) return;

    try {
      const element = videoContainerRef.current;

      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen();
      }
    } catch (error) {
      console.error('Error entering fullscreen:', error);
    }
  };

  // Exit fullscreen mode
  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
    } catch (error) {
      console.error('Error exiting fullscreen:', error);
    }
  };

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    // Add event listeners for different browsers
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange,
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange,
      );
      document.removeEventListener(
        'MSFullscreenChange',
        handleFullscreenChange,
      );
    };
  }, []);

  return (
    <Styled.Root
      ref={videoContainerRef}
      className={pageContext}
      hasVideo={video && video.url}
      orientation={orientation}
      isFullscreen={isFullscreen}
    >
      {video && (
        <VideoPlayer
          videoProps={{
            // Add a scrub forward to the video to ensure the preview thumbnail is shown on mobile
            src: `${video.url}#t=0.001`,
            crossOrigin: 'anonymous',
          }}
          aspectRatio={
            orientation === 'portrait'
              ? ASPECT_RATIOS.PORTRAIT
              : ASPECT_RATIOS.LANDSCAPE
          }
          videoPlayerSize="sm"
          width={orientation === 'portrait' ? '90%' : '100%'}
          height="auto"
          subtitlesUrl={subtitlesFile?.url || null}
          ariaLabels={{
            subtitlesButton: 'Toggle subtitles',
            subtitlesMenu: 'Subtitle options',
          }}
          onExpand={enterFullscreen}
          onMinimize={exitFullscreen}
        />
      )}
      {videoId && videoName && type && (
        <Video
          type={type}
          videoId={videoId}
          videoName={videoName}
          caption={caption}
          verticalVideo={video}
          orientation={orientation}
        />
      )}
    </Styled.Root>
  );
};

VideoSection.propTypes = {
  /**
   * Video Type
   */
  type: PropTypes.oneOf([VIDEO_TYPES.YOUTUBE, VIDEO_TYPES.VIMEO]),
  /**
   * Video Id
   */
  videoId: PropTypes.string,
  /**
   * Video Name
   */
  videoName: PropTypes.string,
  /**
   * An optional caption at the bottom of the video
   */
  caption: PropTypes.string,
  /**
   * Page type
   */
  pageContext: PropTypes.string,
};

export default VideoSection;
